/**
 * Which broker's public microsite should a CUSTOMER be sent to?
 *
 * A single customer login can be linked to several `Customer` rows, one per
 * broker (see apps/customers/views.py `me`, "Item 8: Customer.user is now a
 * ForeignKey"). `/customers/me/` therefore answers an easier question than the
 * one we need: it returns the most recently *created* linked profile, which is
 * an arbitrary broker, not necessarily the one the customer actually deals
 * with. Sending "Buy New Policy" to that broker's site would be a coin flip for
 * anyone with more than one link.
 *
 * There is no authenticated endpoint that lists a customer's brokers, so the
 * evidence used here is:
 *
 *  1. the customer's own policies (`GET /policies/` spans EVERY linked Customer
 *     row -- PolicyViewSet filters on `customer__user`), each carrying `broker`;
 *  2. the profile broker from `/customers/me/`, as a floor for a customer who
 *     has not bought anything yet -- the first-time buyer case;
 *  3. `/policy/api/public/brokers/`, the AllowAny directory, whose queryset is
 *     `status="active", is_public_profile_enabled=True, is_deleted=False`. A
 *     broker being IN that list is exactly the "has a public site" test, and
 *     the row carries the `public_profile_slug` the microsite URL needs.
 *
 * (3) is what stops us handing a customer a 404: a broker that never opted in
 * is simply not a candidate, and when no candidate survives the caller falls
 * back to the in-app catalog.
 */

/** Row from `GET /policy/api/public/brokers/` (AllowAny public directory). */
export interface PublicBrokerRow {
  id: string;
  name?: string;
  public_profile_slug: string | null;
}

/** The only two fields of a policy this resolution cares about. */
export interface PolicyBrokerRef {
  broker?: string | null;
  broker_name?: string | null;
}

export interface MicrositeTarget {
  brokerId: string;
  brokerName: string;
  slug: string;
  /** Absolute path on this host -- the microsite is server-rendered Django. */
  url: string;
}

/**
 * The always-available temp scheme, served under `FORCE_SCRIPT_NAME=/policy`
 * (apps/microsite/CLAUDE.md, "URL schemes"). Deep links to
 * `company/<insurer_slug>/[<policy_slug>/]` are deliberately NOT built here --
 * see the note in BuyPolicyPage.
 */
export function micrositeHomeUrl(slug: string): string {
  return `/policy/site/${encodeURIComponent(slug)}/`;
}

/**
 * Mirrors `django.utils.text.slugify()` closely enough for insurance-type
 * names -- short, mostly-ASCII strings the country admin configures (F6 step
 * 4). `apps/microsite/views_public.py`'s `_accessible_lines` builds the
 * product-line URL slug the exact same way (`slugify(t.name)`, consumed by
 * `urls_public.py`'s `<broker_slug>/insurance/<type_slug>/`), so this has to
 * agree with it token-for-token or the C1 tile link below 404s for every type.
 * NOT `django.utils.text.slugify(allow_unicode=True)` -- the ASCII path,
 * which strips accents rather than keeping them, matching the default the
 * backend actually calls with.
 */
function djangoSlugify(value: string): string {
  const ascii = value.normalize('NFKD').replace(/[\u0300-\u036f]/g, '');
  const stripped = ascii.toLowerCase().replace(/[^\w\s-]/g, '');
  return stripped.replace(/[-\s]+/g, '-').replace(/^[-_]+|[-_]+$/g, '');
}

/**
 * C1: the type-filtered product-line page
 * (`/policy/site/<slug>/insurance/<type_slug>/`), addressed by the
 * INSURANCE-TYPE NAME the same way the backend derives its slug -- NOT by the
 * type `code` the dashboard tiles otherwise key on (DashboardPage's
 * TILE_STYLES/BUILT_IN_INSURANCE_TYPES). Falls back to the microsite home
 * when the name slugifies to nothing (a blank or symbols-only name): a home
 * link is always valid, and a blank-slug URL is not what "no slug" should
 * silently become.
 *
 * This can still 404 for a type the RESOLVED broker does not actually sell --
 * `_accessible_lines` filters to sellable lines per broker (confirmed against
 * `tests/integrations/test_microsite_public_site_2026_08_07.py`'s
 * `test_a_line_the_broker_cannot_sell_404s`), and the type list driving these
 * tiles is platform-wide (no per-broker type API exists to check against
 * first -- see the comment above `insuranceTypesQuery` in DashboardPage.tsx).
 * That is the SAME pre-existing gap the tile list already has today by
 * landing every type on the microsite home; this only narrows the target to
 * the type's own page, which is strictly closer to where a click should land
 * than the home page was. It does not attempt the harder product/company-level
 * deep link BuyPolicyPage's comment describes -- that slug cannot be derived
 * client-side at all.
 */
export function micrositeTypeUrl(slug: string, typeName: string): string {
  const typeSlug = djangoSlugify(typeName);
  return typeSlug ? `${micrositeHomeUrl(slug)}insurance/${encodeURIComponent(typeSlug)}/` : micrositeHomeUrl(slug);
}

interface Candidate {
  brokerId: string;
  brokerName: string;
  slug: string;
  policyCount: number;
  isProfileBroker: boolean;
}

export function resolveCustomerMicrosite({
  policies = [],
  profileBrokerId,
  profileBrokerName,
  publicBrokers = [],
}: {
  policies?: PolicyBrokerRef[];
  profileBrokerId?: string | null;
  profileBrokerName?: string | null;
  publicBrokers?: PublicBrokerRow[];
}): MicrositeTarget | null {
  // Only brokers that opted into a public site are addressable at all.
  const live = new Map<string, PublicBrokerRow>();
  for (const row of publicBrokers) {
    if (row?.id && row.public_profile_slug) live.set(row.id, row);
  }
  if (live.size === 0) return null;

  const counts = new Map<string, number>();
  const names = new Map<string, string>();
  for (const policy of policies) {
    const brokerId = policy?.broker;
    if (!brokerId) continue;
    counts.set(brokerId, (counts.get(brokerId) ?? 0) + 1);
    if (policy.broker_name) names.set(brokerId, policy.broker_name);
  }
  if (profileBrokerId && profileBrokerName) names.set(profileBrokerId, profileBrokerName);

  const candidateIds = new Set<string>(counts.keys());
  if (profileBrokerId) candidateIds.add(profileBrokerId);

  const candidates: Candidate[] = [];
  for (const brokerId of candidateIds) {
    const row = live.get(brokerId);
    if (!row) continue;
    candidates.push({
      brokerId,
      brokerName: names.get(brokerId) || row.name || 'your broker',
      slug: row.public_profile_slug as string,
      policyCount: counts.get(brokerId) ?? 0,
      isProfileBroker: brokerId === profileBrokerId,
    });
  }
  if (candidates.length === 0) return null;

  // Most policies wins -- that is the broker this customer demonstrably buys
  // from. Ties go to the profile broker, so the choice agrees with the rest of
  // the customer UI (My Contacts) whenever the evidence is silent. ("Your
  // Advisor" used to be part of that agreement too; removed from the
  // dashboard by C2, batch 2026-09-18.) Broker id last so the result is
  // stable rather than dependent on response ordering.
  candidates.sort(
    (a, b) =>
      b.policyCount - a.policyCount ||
      Number(b.isProfileBroker) - Number(a.isProfileBroker) ||
      a.brokerName.localeCompare(b.brokerName) ||
      a.brokerId.localeCompare(b.brokerId)
  );

  const winner = candidates[0];
  return {
    brokerId: winner.brokerId,
    brokerName: winner.brokerName,
    slug: winner.slug,
    url: micrositeHomeUrl(winner.slug),
  };
}
